import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import {
  ArrowLeftIcon,
  PhoneIcon,
  UserGroupIcon,
  CalendarIcon,
  Cog6ToothIcon,
  PlusIcon,
  TrashIcon,
  CheckCircleIcon,
  SparklesIcon,
  ExclamationTriangleIcon,
  PauseCircleIcon,
  PlayCircleIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import VoiceSelectionModal from '../components/VoiceSelectionModal';
import { useAgency } from '../context/AgencyContext';
import {
  updateAgencyBusiness,
  listAgencyBusinessMembers,
  addAgencyBusinessMember,
  removeAgencyBusinessMember,
  suspendBusiness,
  unsuspendBusiness,
  deleteBusinessWithOptions,
  // Retell API
  listRetellVoices,
  setupRetellAgent,
  getRetellStatus,
  syncRetellAgent,
  deleteRetellAgent,
  purchaseRetellPhoneNumber,
  releaseRetellPhoneNumber,
  RetellVoice,
  RetellAgentStatus,
} from '../services/api';
import { Business, BusinessMember, Service, FAQ } from '../types';

const DEFAULT_SYSTEM_PROMPT = `## Role

You are {{business_name}}'s AI phone receptionist. Your job is to answer calls professionally, help callers with their needs, and collect information when needed.

## Your Tasks

1. **Answer calls warmly** - Greet callers and identify their needs
2. **Answer questions** - Use the FAQ knowledge base to answer common questions
3. **Schedule appointments** - Check availability and book appointments
4. **Take messages** - Collect caller information for follow-up
5. **Transfer calls** - Connect callers to a human when requested or when you can't help

## Guidelines

- Be friendly, professional, and concise
- Don't make up information you don't have
- If unsure, offer to take a message or transfer to a human
- Confirm important details (names, phone numbers, appointment times)
- Keep responses brief - this is a phone call, not a chat

## Business Information

- Business: {{business_name}}
- Hours: Use the business hours configuration
- Services: Use the services list to explain what's offered
- FAQs: Reference the FAQ list for common questions

## Conversation Flow

1. Greeting: "Hello, thank you for calling {{business_name}}. How may I help you today?"
2. Listen and identify intent
3. Handle the request or ask clarifying questions
4. Confirm any actions taken
5. Ask if there's anything else
6. Thank them and end the call politely`;

export default function ClientDetailPage() {
  const { businessId } = useParams<{ businessId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentAgency, businesses, refreshBusinesses } = useAgency();

  // Allow tab to be set via query param (e.g., ?tab=phone after client creation)
  const initialTab = searchParams.get('tab') || 'overview';
  const [activeTab, setActiveTab] = useState(initialTab);

  const business = businesses.find((b) => b.id === businessId);

  if (!currentAgency) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Please select an agency first.</p>
      </div>
    );
  }

  if (!business) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Business not found.</p>
        <Link to="/clients" className="text-primary-600 hover:underline mt-2 inline-block">
          Back to Clients
        </Link>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', name: 'Overview', icon: Cog6ToothIcon },
    { id: 'phone', name: 'Phone Number', icon: PhoneIcon },
    { id: 'ai', name: 'AI Configuration', icon: SparklesIcon },
    { id: 'general', name: 'General', icon: Cog6ToothIcon },
    { id: 'hours', name: 'Business Hours', icon: CalendarIcon },
    { id: 'services', name: 'Services', icon: CalendarIcon },
    { id: 'faqs', name: 'FAQs', icon: UserGroupIcon },
    { id: 'voice', name: 'Voice', icon: PhoneIcon },
    { id: 'team', name: 'Team', icon: UserGroupIcon },
    { id: 'advanced', name: 'Advanced', icon: Cog6ToothIcon },
  ];

  return (
    <div>
      <div className="mb-6">
        <button
          onClick={() => navigate('/clients')}
          className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-1" />
          Back to Clients
        </button>
      </div>

      <div className="sm:flex sm:items-center sm:justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{business.name}</h1>
          <p className="mt-1 text-sm text-gray-500">{business.phoneNumber}</p>
        </div>
        <div className="mt-4 sm:mt-0 flex gap-3">
          <Link
            to={`/clients/${businessId}/calls`}
            className="inline-flex items-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
          >
            <PhoneIcon className="h-4 w-4 mr-1.5" />
            View Calls
          </Link>
          <Link
            to={`/clients/${businessId}/leads`}
            className="inline-flex items-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
          >
            <UserGroupIcon className="h-4 w-4 mr-1.5" />
            View Leads
          </Link>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                activeTab === tab.id
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      <div className="bg-white shadow rounded-lg p-6">
        {activeTab === 'overview' && (
          <OverviewTab
            business={business}
            agencyId={currentAgency.id}
            onUpdate={refreshBusinesses}
          />
        )}
        {activeTab === 'phone' && (
          <PhoneTab
            business={business}
            agencyId={currentAgency.id}
            onUpdate={refreshBusinesses}
          />
        )}
        {activeTab === 'ai' && (
          <AIConfigurationTab
            business={business}
            agencyId={currentAgency.id}
            onUpdate={refreshBusinesses}
          />
        )}
        {activeTab === 'general' && (
          <GeneralSettingsTab
            business={business}
            agencyId={currentAgency.id}
            onUpdate={refreshBusinesses}
          />
        )}
        {activeTab === 'hours' && (
          <BusinessHoursTab
            business={business}
            agencyId={currentAgency.id}
            onUpdate={refreshBusinesses}
          />
        )}
        {activeTab === 'services' && (
          <ServicesTab
            business={business}
            agencyId={currentAgency.id}
            onUpdate={refreshBusinesses}
          />
        )}
        {activeTab === 'faqs' && (
          <FAQsTab
            business={business}
            agencyId={currentAgency.id}
            onUpdate={refreshBusinesses}
          />
        )}
        {activeTab === 'voice' && (
          <VoiceTab
            business={business}
            agencyId={currentAgency.id}
            onUpdate={refreshBusinesses}
          />
        )}
        {activeTab === 'team' && (
          <TeamTab business={business} agencyId={currentAgency.id} />
        )}
        {activeTab === 'advanced' && (
          <AdvancedTab
            business={business}
            agencyId={currentAgency.id}
            onUpdate={refreshBusinesses}
          />
        )}
      </div>
    </div>
  );
}

function OverviewTab({
  business,
  agencyId,
  onUpdate,
}: {
  business: Business;
  agencyId: string;
  onUpdate: () => void;
}) {
  const navigate = useNavigate();
  const [suspending, setSuspending] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [retellStatus, setRetellStatus] = useState<RetellAgentStatus | null>(null);

  useEffect(() => {
    const loadStatus = async () => {
      try {
        const status = await getRetellStatus(agencyId, business.id);
        setRetellStatus(status);
      } catch (error) {
        console.error('Error loading Retell status:', error);
      }
    };
    loadStatus();
  }, [agencyId, business.id]);

  const handleToggleSuspension = async () => {
    if (!confirm(
      business.status === 'suspended'
        ? 'Unsuspend this business? They will start receiving calls again.'
        : 'Suspend this business? They will stop receiving calls until unsuspended.'
    )) {
      return;
    }

    setSuspending(true);
    try {
      if (business.status === 'suspended') {
        await unsuspendBusiness(agencyId, business.id);
      } else {
        await suspendBusiness(agencyId, business.id, 'Suspended by agency admin');
      }
      onUpdate();
    } catch (error: any) {
      alert(error.message || 'Failed to update business status');
    } finally {
      setSuspending(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Suspension Warning */}
      {business.status === 'suspended' && (
        <div className="rounded-md bg-red-50 border border-red-200 p-4">
          <div className="flex">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Business Suspended</h3>
              <p className="mt-1 text-sm text-red-700">
                This business is currently suspended and is not receiving calls.
                {business.suspendedReason && ` Reason: ${business.suspendedReason}`}
              </p>
            </div>
          </div>
        </div>
      )}

      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Quick Stats</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm text-gray-500">Total Calls (30d)</p>
            <p className="text-2xl font-semibold text-gray-900">
              {business.stats?.totalCallsThisMonth || 0}
            </p>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm text-gray-500">Total Leads</p>
            <p className="text-2xl font-semibold text-gray-900">
              {business.stats?.totalLeads || 0}
            </p>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm text-gray-500">Total Appointments</p>
            <p className="text-2xl font-semibold text-gray-900">
              {business.stats?.totalAppointments || 0}
            </p>
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Configuration Status</h3>
        <div className="space-y-3">
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <span className="text-sm text-gray-700">AI Voice Agent</span>
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
              retellStatus?.hasAgent ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
            }`}>
              {retellStatus?.hasAgent ? 'Active' : 'Not Set Up'}
            </span>
          </div>
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <span className="text-sm text-gray-700">Phone Number</span>
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
              retellStatus?.hasPhoneNumber ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
            }`}>
              {retellStatus?.hasPhoneNumber ? retellStatus.phoneNumber : 'Not Assigned'}
            </span>
          </div>
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <span className="text-sm text-gray-700">AI System Prompt</span>
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
              business.systemPrompt ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
            }`}>
              {business.systemPrompt ? 'Configured' : 'Using Default'}
            </span>
          </div>
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <span className="text-sm text-gray-700">Services</span>
            <span className="text-sm text-gray-900">{business.services?.length || 0} configured</span>
          </div>
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <span className="text-sm text-gray-700">FAQs</span>
            <span className="text-sm text-gray-900">{business.faqs?.length || 0} configured</span>
          </div>
        </div>
      </div>

      {/* Business Actions */}
      <div className="border-t pt-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Business Actions</h3>
        <div className="space-y-4">
          {/* Suspend/Unsuspend */}
          <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
            <div className="flex items-center gap-3">
              {business.status === 'suspended' ? (
                <PlayCircleIcon className="h-6 w-6 text-green-500" />
              ) : (
                <PauseCircleIcon className="h-6 w-6 text-yellow-500" />
              )}
              <div>
                <p className="font-medium text-gray-900">
                  {business.status === 'suspended' ? 'Unsuspend Business' : 'Suspend Business'}
                </p>
                <p className="text-sm text-gray-500">
                  {business.status === 'suspended'
                    ? 'Resume receiving calls for this business'
                    : 'Temporarily stop receiving calls for this business'}
                </p>
              </div>
            </div>
            <button
              onClick={handleToggleSuspension}
              disabled={suspending}
              className={`px-4 py-2 rounded-md text-sm font-medium ${
                business.status === 'suspended'
                  ? 'bg-green-600 text-white hover:bg-green-700'
                  : 'bg-yellow-600 text-white hover:bg-yellow-700'
              } disabled:opacity-50`}
            >
              {suspending ? 'Processing...' : business.status === 'suspended' ? 'Unsuspend' : 'Suspend'}
            </button>
          </div>

          {/* Delete Business */}
          <div className="flex items-center justify-between p-4 border border-red-200 rounded-lg bg-red-50">
            <div className="flex items-center gap-3">
              <TrashIcon className="h-6 w-6 text-red-500" />
              <div>
                <p className="font-medium text-red-900">Delete Business</p>
                <p className="text-sm text-red-700">
                  Permanently remove this business and all associated data
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowDeleteModal(true)}
              className="px-4 py-2 bg-red-600 text-white rounded-md text-sm font-medium hover:bg-red-700"
            >
              Delete
            </button>
          </div>
        </div>
      </div>

      {/* Delete Modal */}
      {showDeleteModal && (
        <DeleteBusinessModal
          business={business}
          agencyId={agencyId}
          onClose={() => setShowDeleteModal(false)}
          onDeleted={async () => {
            await onUpdate();
            navigate('/clients');
          }}
        />
      )}
    </div>
  );
}

function PhoneTab({
  business,
  agencyId,
  onUpdate,
}: {
  business: Business;
  agencyId: string;
  onUpdate: () => void;
}) {
  const [retellStatus, setRetellStatus] = useState<RetellAgentStatus | null>(null);
  const [areaCode, setAreaCode] = useState('');
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  const [releasing, setReleasing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadStatus();
  }, [agencyId, business.id]);

  const loadStatus = async () => {
    setLoading(true);
    try {
      const status = await getRetellStatus(agencyId, business.id);
      setRetellStatus(status);
    } catch (err) {
      console.error('Error loading Retell status:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePurchaseNumber = async () => {
    if (!retellStatus?.hasAgent) {
      setError('Please set up the AI agent first in the Voice tab');
      return;
    }

    setPurchasing(true);
    setError(null);
    try {
      await purchaseRetellPhoneNumber(agencyId, business.id, areaCode || undefined, business.name);
      await loadStatus();
      onUpdate();
    } catch (err: any) {
      setError(err.message || 'Failed to purchase phone number');
    } finally {
      setPurchasing(false);
    }
  };

  const handleReleaseNumber = async () => {
    if (!confirm('Release this phone number? This action cannot be undone and you will lose this number.')) {
      return;
    }

    setReleasing(true);
    setError(null);
    try {
      await releaseRetellPhoneNumber(agencyId, business.id);
      await loadStatus();
      onUpdate();
    } catch (err: any) {
      setError(err.message || 'Failed to release phone number');
    } finally {
      setReleasing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">Phone Number</h3>
        <p className="text-sm text-gray-500">
          Manage the phone number for this client. Callers will dial this number to reach the AI receptionist.
        </p>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 p-4">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Agent Status Check */}
      {!retellStatus?.hasAgent && (
        <div className="rounded-md bg-yellow-50 border border-yellow-200 p-4">
          <div className="flex">
            <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">AI Agent Required</h3>
              <p className="mt-1 text-sm text-yellow-700">
                You need to set up the AI agent first before purchasing a phone number.
                Go to the <strong>Voice</strong> tab to configure the AI agent.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Current Phone Number */}
      {retellStatus?.hasPhoneNumber && retellStatus.phoneNumber ? (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-green-800">Active Phone Number</p>
              <p className="text-2xl font-bold text-green-900 mt-1">{retellStatus.phoneNumber}</p>
              <p className="text-sm text-green-700 mt-1">
                Powered by Retell AI
              </p>
            </div>
            <button
              onClick={handleReleaseNumber}
              disabled={releasing}
              className="text-red-600 hover:text-red-700 text-sm font-medium disabled:opacity-50"
            >
              {releasing ? 'Releasing...' : 'Release Number'}
            </button>
          </div>
        </div>
      ) : retellStatus?.hasAgent ? (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-sm font-medium text-yellow-800">No Phone Number Assigned</p>
          <p className="text-sm text-yellow-700 mt-1">
            Purchase a phone number below to start receiving calls.
          </p>
        </div>
      ) : null}

      {/* Purchase Phone Number */}
      {retellStatus?.hasAgent && !retellStatus.hasPhoneNumber && (
        <div className="border-t pt-6">
          <h4 className="text-md font-medium text-gray-900 mb-4">Purchase Phone Number</h4>
          <p className="text-sm text-gray-500 mb-4">
            Get a dedicated phone number for this business. Callers will dial this number to reach the AI receptionist.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 mb-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Area Code (optional)
              </label>
              <input
                type="text"
                value={areaCode}
                onChange={(e) => setAreaCode(e.target.value.replace(/\D/g, '').slice(0, 3))}
                placeholder="e.g., 512"
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                maxLength={3}
              />
              <p className="text-xs text-gray-500 mt-1">Leave empty for any available area code</p>
            </div>

            <div className="flex items-end">
              <button
                onClick={handlePurchaseNumber}
                disabled={purchasing}
                className="w-full sm:w-auto px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50"
              >
                {purchasing ? 'Purchasing...' : 'Purchase Number'}
              </button>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              <strong>Note:</strong> Phone numbers are billed directly through Retell AI.
              Check your Retell dashboard for pricing details.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

function AIConfigurationTab({
  business,
  agencyId,
  onUpdate,
}: {
  business: Business;
  agencyId: string;
  onUpdate: () => void;
}) {
  const [systemPrompt, setSystemPrompt] = useState(
    business.systemPrompt || DEFAULT_SYSTEM_PROMPT.replace(/\{\{business_name\}\}/g, business.name)
  );
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await updateAgencyBusiness(agencyId, business.id, { systemPrompt });
      onUpdate();
      alert('AI configuration saved!');
    } catch (error) {
      console.error('Error updating AI configuration:', error);
      alert('Failed to save AI configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleResetToDefault = () => {
    if (confirm('Reset to the default system prompt? This will overwrite your current prompt.')) {
      setSystemPrompt(DEFAULT_SYSTEM_PROMPT.replace(/\{\{business_name\}\}/g, business.name));
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-medium text-gray-700">
            System Prompt
          </label>
          <button
            type="button"
            onClick={handleResetToDefault}
            className="text-sm text-gray-500 hover:text-gray-700"
          >
            Reset to Default
          </button>
        </div>
        <p className="text-sm text-gray-500 mb-3">
          This prompt defines how the AI receptionist behaves. It sets the personality,
          capabilities, and guidelines for handling calls.
        </p>
        <textarea
          className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 font-mono text-sm"
          rows={25}
          value={systemPrompt}
          onChange={(e) => setSystemPrompt(e.target.value)}
        />
        <p className="mt-2 text-xs text-gray-500">
          Use <code className="bg-gray-100 px-1 rounded">{'{{business_name}}'}</code> to insert the business name dynamically.
        </p>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="text-sm font-medium text-blue-900 mb-2">Tips for effective prompts:</h4>
        <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
          <li>Define the AI's role and personality clearly</li>
          <li>List specific tasks the AI should handle</li>
          <li>Include guidelines for edge cases</li>
          <li>Specify when to transfer to a human</li>
          <li>Keep responses concise - it's a phone call</li>
        </ul>
      </div>

      <button type="submit" disabled={loading} className="btn-primary">
        {loading ? 'Saving...' : 'Save AI Configuration'}
      </button>
    </form>
  );
}

function GeneralSettingsTab({
  business,
  agencyId,
  onUpdate,
}: {
  business: Business;
  agencyId: string;
  onUpdate: () => void;
}) {
  const [formData, setFormData] = useState({
    name: business.name,
    phoneNumber: business.phoneNumber,
    timezone: business.timezone,
    greeting: business.greeting,
    transferNumber: business.transferNumber || '',
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await updateAgencyBusiness(agencyId, business.id, formData);
      onUpdate();
      alert('Settings saved!');
    } catch (error) {
      console.error('Error updating business:', error);
      alert('Failed to save settings');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700">Business Name</label>
        <input
          type="text"
          required
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700">Phone Number</label>
        <input
          type="tel"
          required
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
          value={formData.phoneNumber}
          onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
        />
        <p className="text-xs text-gray-500 mt-1">Your AI receptionist phone number (managed via Retell)</p>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700">Timezone</label>
        <select
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
          value={formData.timezone}
          onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
        >
          <option value="America/New_York">Eastern Time</option>
          <option value="America/Chicago">Central Time</option>
          <option value="America/Denver">Mountain Time</option>
          <option value="America/Los_Angeles">Pacific Time</option>
        </select>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700">Greeting Message</label>
        <textarea
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
          rows={3}
          value={formData.greeting}
          onChange={(e) => setFormData({ ...formData, greeting: e.target.value })}
        />
        <p className="text-xs text-gray-500 mt-1">This message will be spoken when answering calls</p>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700">Transfer Number (optional)</label>
        <input
          type="tel"
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
          value={formData.transferNumber}
          onChange={(e) => setFormData({ ...formData, transferNumber: e.target.value })}
        />
        <p className="text-xs text-gray-500 mt-1">Calls will be transferred here when requested</p>
      </div>

      <button type="submit" disabled={loading} className="btn-primary">
        {loading ? 'Saving...' : 'Save Changes'}
      </button>
    </form>
  );
}

function BusinessHoursTab({
  business,
  agencyId,
  onUpdate,
}: {
  business: Business;
  agencyId: string;
  onUpdate: () => void;
}) {
  const [businessHours, setBusinessHours] = useState(business.businessHours || {
    monday: { open: '09:00', close: '17:00', closed: false },
    tuesday: { open: '09:00', close: '17:00', closed: false },
    wednesday: { open: '09:00', close: '17:00', closed: false },
    thursday: { open: '09:00', close: '17:00', closed: false },
    friday: { open: '09:00', close: '17:00', closed: false },
    saturday: { open: '09:00', close: '17:00', closed: true },
    sunday: { open: '09:00', close: '17:00', closed: true },
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await updateAgencyBusiness(agencyId, business.id, { businessHours });
      onUpdate();
      alert('Business hours saved!');
    } catch (error) {
      console.error('Error updating business hours:', error);
      alert('Failed to save business hours');
    } finally {
      setLoading(false);
    }
  };

  const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-4">
        {days.map((day) => {
          const hours = businessHours[day] || { open: '09:00', close: '17:00', closed: false };
          return (
            <div key={day} className="flex items-center gap-4">
              <div className="w-28 capitalize text-sm font-medium text-gray-700">{day}</div>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={!hours.closed}
                  onChange={(e) =>
                    setBusinessHours({
                      ...businessHours,
                      [day]: { ...hours, closed: !e.target.checked },
                    })
                  }
                  className="h-4 w-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
                />
                <span className="text-sm text-gray-600">Open</span>
              </label>
              {!hours.closed && (
                <>
                  <input
                    type="time"
                    className="rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                    value={hours.open}
                    onChange={(e) =>
                      setBusinessHours({
                        ...businessHours,
                        [day]: { ...hours, open: e.target.value },
                      })
                    }
                  />
                  <span className="text-gray-500">to</span>
                  <input
                    type="time"
                    className="rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                    value={hours.close}
                    onChange={(e) =>
                      setBusinessHours({
                        ...businessHours,
                        [day]: { ...hours, close: e.target.value },
                      })
                    }
                  />
                </>
              )}
            </div>
          );
        })}
      </div>
      <button type="submit" disabled={loading} className="btn-primary">
        {loading ? 'Saving...' : 'Save Business Hours'}
      </button>
    </form>
  );
}

function ServicesTab({
  business,
  agencyId,
  onUpdate,
}: {
  business: Business;
  agencyId: string;
  onUpdate: () => void;
}) {
  const [services, setServices] = useState<Service[]>(business.services || []);
  const [loading, setLoading] = useState(false);

  const addService = () => {
    setServices([
      ...services,
      {
        id: Date.now().toString(),
        name: '',
        description: '',
        duration: 60,
        price: undefined,
      },
    ]);
  };

  const removeService = (id: string) => {
    setServices(services.filter((s) => s.id !== id));
  };

  const updateService = (id: string, updates: Partial<Service>) => {
    setServices(services.map((s) => (s.id === id ? { ...s, ...updates } : s)));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await updateAgencyBusiness(agencyId, business.id, { services });
      onUpdate();
      alert('Services saved!');
    } catch (error) {
      console.error('Error updating services:', error);
      alert('Failed to save services');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <p className="text-sm text-gray-600">
        Services are used by the AI to explain what the business offers and to book appointments.
      </p>
      <div className="space-y-4">
        {services.map((service, index) => (
          <div key={service.id} className="border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-medium">Service {index + 1}</h3>
              <button
                type="button"
                onClick={() => removeService(service.id)}
                className="text-red-600 hover:text-red-700"
              >
                <TrashIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Name</label>
                <input
                  type="text"
                  required
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                  value={service.name}
                  onChange={(e) => updateService(service.id, { name: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Duration (minutes)
                  <span className="text-gray-400 font-normal ml-1">- optional</span>
                </label>
                <input
                  type="number"
                  min="15"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                  value={service.duration || ''}
                  onChange={(e) =>
                    updateService(service.id, {
                      duration: e.target.value ? parseInt(e.target.value) : undefined,
                    })
                  }
                />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700">Description</label>
                <textarea
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                  rows={2}
                  value={service.description}
                  onChange={(e) => updateService(service.id, { description: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Price
                  <span className="text-gray-400 font-normal ml-1">- optional</span>
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                  value={service.price || ''}
                  onChange={(e) =>
                    updateService(service.id, {
                      price: e.target.value ? parseFloat(e.target.value) : undefined,
                    })
                  }
                />
              </div>
            </div>
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={addService}
        className="inline-flex items-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
      >
        <PlusIcon className="h-5 w-5 mr-1.5" />
        Add Service
      </button>
      <div>
        <button type="submit" disabled={loading} className="btn-primary">
          {loading ? 'Saving...' : 'Save Services'}
        </button>
      </div>
    </form>
  );
}

function FAQsTab({
  business,
  agencyId,
  onUpdate,
}: {
  business: Business;
  agencyId: string;
  onUpdate: () => void;
}) {
  const [faqs, setFaqs] = useState<FAQ[]>(business.faqs || []);
  const [loading, setLoading] = useState(false);

  const addFaq = () => {
    setFaqs([
      ...faqs,
      {
        id: Date.now().toString(),
        question: '',
        answer: '',
        keywords: [],
      },
    ]);
  };

  const removeFaq = (id: string) => {
    setFaqs(faqs.filter((f) => f.id !== id));
  };

  const updateFaq = (id: string, updates: Partial<FAQ>) => {
    setFaqs(faqs.map((f) => (f.id === id ? { ...f, ...updates } : f)));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await updateAgencyBusiness(agencyId, business.id, { faqs });
      onUpdate();
      alert('FAQs saved!');
    } catch (error) {
      console.error('Error updating FAQs:', error);
      alert('Failed to save FAQs');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <p className="text-sm text-gray-600">
        FAQs help the AI answer common questions accurately. The AI will reference these when callers ask related questions.
      </p>
      <div className="space-y-4">
        {faqs.map((faq, index) => (
          <div key={faq.id} className="border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-medium">FAQ {index + 1}</h3>
              <button
                type="button"
                onClick={() => removeFaq(faq.id)}
                className="text-red-600 hover:text-red-700"
              >
                <TrashIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Question</label>
                <input
                  type="text"
                  required
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                  value={faq.question}
                  onChange={(e) => updateFaq(faq.id, { question: e.target.value })}
                  placeholder="What are your hours?"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Answer</label>
                <textarea
                  required
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                  rows={3}
                  value={faq.answer}
                  onChange={(e) => updateFaq(faq.id, { answer: e.target.value })}
                  placeholder="We're open Monday through Friday from 9 AM to 5 PM..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Keywords
                  <span className="text-gray-400 font-normal ml-1">- comma-separated</span>
                </label>
                <input
                  type="text"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                  value={faq.keywords.join(', ')}
                  onChange={(e) =>
                    updateFaq(faq.id, {
                      keywords: e.target.value.split(',').map((k) => k.trim()).filter(Boolean),
                    })
                  }
                  placeholder="hours, schedule, open, close"
                />
              </div>
            </div>
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={addFaq}
        className="inline-flex items-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
      >
        <PlusIcon className="h-5 w-5 mr-1.5" />
        Add FAQ
      </button>
      <div>
        <button type="submit" disabled={loading} className="btn-primary">
          {loading ? 'Saving...' : 'Save FAQs'}
        </button>
      </div>
    </form>
  );
}

function VoiceTab({
  business,
  agencyId,
  onUpdate,
}: {
  business: Business;
  agencyId: string;
  onUpdate: () => void;
}) {
  const [voiceId, setVoiceId] = useState(business.voiceId || '11labs-Adrian');
  const [voices, setVoices] = useState<RetellVoice[]>([]);
  const [retellStatus, setRetellStatus] = useState<RetellAgentStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [settingUp, setSettingUp] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showVoiceModal, setShowVoiceModal] = useState(false);

  // Get the selected voice details
  const selectedVoice = voices.find((v) => v.voice_id === voiceId);

  // Clear success message after 5 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [voicesData, status] = await Promise.all([
          listRetellVoices(agencyId),
          getRetellStatus(agencyId, business.id),
        ]);
        setVoices(voicesData);
        setRetellStatus(status);
        if (status.voiceId) {
          setVoiceId(status.voiceId);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoadingData(false);
      }
    };
    fetchData();
  }, [agencyId, business.id]);

  const handleSetupAgent = async () => {
    setSettingUp(true);
    setError(null);
    setSuccessMessage(null);
    try {
      await setupRetellAgent(agencyId, business.id, voiceId);
      // Small delay to ensure Retell API has registered the agent
      await new Promise(resolve => setTimeout(resolve, 500));
      const status = await getRetellStatus(agencyId, business.id);
      setRetellStatus(status);
      onUpdate();
      setSuccessMessage('AI Agent created successfully!');
    } catch (err: any) {
      setError(err.message || 'Failed to set up AI agent');
    } finally {
      setSettingUp(false);
    }
  };

  const handleSyncAgent = async () => {
    setSyncing(true);
    setError(null);
    setSuccessMessage(null);
    try {
      await syncRetellAgent(agencyId, business.id);
      onUpdate();
      setSuccessMessage('AI Agent synced with business configuration!');
    } catch (err: any) {
      setError(err.message || 'Failed to sync AI agent');
    } finally {
      setSyncing(false);
    }
  };

  const handleDeleteAgent = async () => {
    if (!confirm('Delete the AI agent? This will also release any associated phone number.')) {
      return;
    }
    setDeleting(true);
    setError(null);
    setSuccessMessage(null);
    try {
      await deleteRetellAgent(agencyId, business.id);
      setRetellStatus({ hasAgent: false, hasPhoneNumber: false });
      onUpdate();
      setSuccessMessage('AI Agent deleted.');
    } catch (err: any) {
      setError(err.message || 'Failed to delete AI agent');
    } finally {
      setDeleting(false);
    }
  };

  const handleVoiceSelect = (voice: RetellVoice) => {
    setVoiceId(voice.voice_id);
  };

  const handleUpdateVoice = async () => {
    if (!retellStatus?.hasAgent) {
      setError('Please set up the AI agent first');
      return;
    }
    setLoading(true);
    setError(null);
    setSuccessMessage(null);
    try {
      await updateAgencyBusiness(agencyId, business.id, { voiceId });
      await syncRetellAgent(agencyId, business.id);
      onUpdate();
      setSuccessMessage('Voice settings saved and synced!');
    } catch (error: any) {
      console.error('Error updating voice:', error);
      setError(error.message || 'Failed to save voice settings');
    } finally {
      setLoading(false);
    }
  };

  if (loadingData) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">AI Voice Agent</h3>
        <p className="text-sm text-gray-500">
          Configure the AI voice agent powered by Retell AI. The agent handles incoming calls and responds using your business configuration.
        </p>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 p-4">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {successMessage && (
        <div className="rounded-md bg-green-50 border border-green-200 p-4">
          <div className="flex items-center gap-2">
            <CheckCircleIcon className="h-5 w-5 text-green-600" />
            <p className="text-sm text-green-700">{successMessage}</p>
          </div>
        </div>
      )}

      {/* Agent Status */}
      {retellStatus?.hasAgent ? (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2">
                <CheckCircleIcon className="h-5 w-5 text-green-600" />
                <p className="text-sm font-medium text-green-800">AI Agent Active</p>
              </div>
              <p className="text-sm text-green-700 mt-1">
                {retellStatus.agentName || 'Retell AI Agent'}
              </p>
              <p className="text-xs text-green-600 mt-1">
                Agent ID: <span className="font-mono">{retellStatus.agentId}</span>
              </p>
            </div>
            <div className="flex flex-col gap-2">
              <button
                onClick={handleSyncAgent}
                disabled={syncing}
                className="text-primary-600 hover:text-primary-700 text-sm font-medium flex items-center gap-1 disabled:opacity-50"
              >
                <ArrowPathIcon className="h-4 w-4" />
                {syncing ? 'Syncing...' : 'Sync Config'}
              </button>
              <button
                onClick={handleDeleteAgent}
                disabled={deleting}
                className="text-red-600 hover:text-red-700 text-sm font-medium disabled:opacity-50"
              >
                {deleting ? 'Deleting...' : 'Delete Agent'}
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500" />
            <p className="text-sm font-medium text-yellow-800">No AI Agent Configured</p>
          </div>
          <p className="text-sm text-yellow-700 mb-4">
            Create an AI agent to start receiving calls. The agent will use your business's greeting, services, FAQs, and hours to respond to callers.
          </p>

          {/* Voice Selection for new agent */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Select Voice</label>
            <button
              type="button"
              onClick={() => setShowVoiceModal(true)}
              className="w-full flex items-center justify-between p-4 bg-white border-2 border-gray-200 rounded-lg hover:border-primary-300 transition-colors"
            >
              <div className="text-left">
                {selectedVoice ? (
                  <>
                    <p className="font-medium text-gray-900">{selectedVoice.voice_name}</p>
                    <p className="text-sm text-gray-500">
                      {selectedVoice.provider}
                      {selectedVoice.gender && ` • ${selectedVoice.gender}`}
                      {selectedVoice.accent && ` • ${selectedVoice.accent}`}
                    </p>
                  </>
                ) : (
                  <p className="text-gray-500">Click to select a voice</p>
                )}
              </div>
              <span className="text-primary-600 font-medium">Browse Voices</span>
            </button>
          </div>

          <button
            onClick={handleSetupAgent}
            disabled={settingUp}
            className="w-full sm:w-auto px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50"
          >
            {settingUp ? 'Creating Agent...' : 'Create AI Agent'}
          </button>
        </div>
      )}

      {/* Voice Settings (when agent exists) */}
      {retellStatus?.hasAgent && (
        <div className="border-t pt-6">
          <h4 className="text-md font-medium text-gray-900 mb-4">Voice Settings</h4>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Current Voice</label>
            <button
              type="button"
              onClick={() => setShowVoiceModal(true)}
              className="w-full flex items-center justify-between p-4 bg-white border-2 border-gray-200 rounded-lg hover:border-primary-300 transition-colors"
            >
              <div className="text-left">
                {selectedVoice ? (
                  <>
                    <p className="font-medium text-gray-900">{selectedVoice.voice_name}</p>
                    <p className="text-sm text-gray-500">
                      {selectedVoice.provider}
                      {selectedVoice.gender && ` • ${selectedVoice.gender}`}
                      {selectedVoice.accent && ` • ${selectedVoice.accent}`}
                    </p>
                  </>
                ) : (
                  <>
                    <p className="font-medium text-gray-900">{voiceId}</p>
                    <p className="text-sm text-gray-500">Current voice</p>
                  </>
                )}
              </div>
              <span className="text-primary-600 font-medium">Change Voice</span>
            </button>
            <p className="text-xs text-gray-500 mt-2">
              Click to browse and preview available voices
            </p>
          </div>
          <button
            onClick={handleUpdateVoice}
            disabled={loading}
            className="mt-4 btn-primary"
          >
            {loading ? 'Saving...' : 'Update Voice'}
          </button>
        </div>
      )}

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="text-sm font-medium text-blue-900 mb-2">How it works</h4>
        <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
          <li>The AI agent uses your business configuration (greeting, services, FAQs, hours)</li>
          <li>When you update business settings, click "Sync Config" to update the agent</li>
          <li>After creating an agent, you can purchase a phone number in the Phone tab</li>
          <li>Voice AI is powered by Retell AI for natural-sounding conversations</li>
        </ul>
      </div>

      {/* Voice Selection Modal */}
      <VoiceSelectionModal
        isOpen={showVoiceModal}
        onClose={() => setShowVoiceModal(false)}
        onSelect={handleVoiceSelect}
        voices={voices}
        selectedVoiceId={voiceId}
      />
    </div>
  );
}

function TeamTab({ business, agencyId }: { business: Business; agencyId: string }) {
  const [members, setMembers] = useState<BusinessMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);

  useEffect(() => {
    const fetchMembers = async () => {
      try {
        const data = await listAgencyBusinessMembers(agencyId, business.id);
        setMembers(data);
      } catch (error) {
        console.error('Error fetching members:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchMembers();
  }, [agencyId, business.id]);

  const handleRemove = async (memberId: string) => {
    if (!confirm('Are you sure you want to remove this member?')) return;

    try {
      await removeAgencyBusinessMember(agencyId, business.id, memberId);
      setMembers(members.filter((m) => m.userId !== memberId));
    } catch (error) {
      console.error('Error removing member:', error);
      alert('Failed to remove member');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-medium">Team Members</h3>
          <p className="text-sm text-gray-500">People who can access this business in the client portal</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="inline-flex items-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
        >
          <PlusIcon className="h-5 w-5 mr-1.5" />
          Add Member
        </button>
      </div>

      {loading ? (
        <div className="animate-pulse space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-16 bg-gray-200 rounded"></div>
          ))}
        </div>
      ) : members.length === 0 ? (
        <p className="text-sm text-gray-500">No team members yet.</p>
      ) : (
        <div className="space-y-4">
          {members.map((member) => (
            <div
              key={member.userId}
              className="flex items-center justify-between p-4 border border-gray-200 rounded-lg"
            >
              <div>
                <p className="font-medium">{member.name}</p>
                <p className="text-sm text-gray-500">{member.email}</p>
              </div>
              <div className="flex items-center gap-4">
                <span
                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    member.role === 'owner'
                      ? 'bg-purple-100 text-purple-800'
                      : member.role === 'admin'
                      ? 'bg-blue-100 text-blue-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}
                >
                  {member.role}
                </span>
                {member.role !== 'owner' && (
                  <button
                    onClick={() => handleRemove(member.userId)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <TrashIcon className="h-5 w-5" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {showAddModal && (
        <AddMemberModal
          agencyId={agencyId}
          businessId={business.id}
          onClose={() => setShowAddModal(false)}
          onAdded={(member) => {
            setMembers([...members, member]);
            setShowAddModal(false);
          }}
        />
      )}
    </div>
  );
}

function AddMemberModal({
  agencyId,
  businessId,
  onClose,
  onAdded,
}: {
  agencyId: string;
  businessId: string;
  onClose: () => void;
  onAdded: (member: BusinessMember) => void;
}) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('member');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const member = await addAgencyBusinessMember(agencyId, businessId, email, role);
      onAdded(member);
    } catch (error: any) {
      console.error('Error adding member:', error);
      alert(error.message || 'Failed to add member');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={onClose}></div>
        <div className="relative bg-white rounded-lg max-w-md w-full p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Add Team Member</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Email</label>
              <input
                type="email"
                required
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <p className="text-xs text-gray-500 mt-1">User must have an existing account</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Role</label>
              <select
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                value={role}
                onChange={(e) => setRole(e.target.value)}
              >
                <option value="member">Member</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                type="button"
                onClick={onClose}
                className="inline-flex items-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button type="submit" disabled={loading} className="btn-primary">
                {loading ? 'Adding...' : 'Add Member'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function AdvancedTab({
  business,
  agencyId,
  onUpdate,
}: {
  business: Business;
  agencyId: string;
  onUpdate: () => void;
}) {
  const [maxCallDuration, setMaxCallDuration] = useState(business.settings?.maxCallDuration || 600);
  const [loading, setLoading] = useState(false);
  const [retellStatus, setRetellStatus] = useState<RetellAgentStatus | null>(null);
  const [loadingStatus, setLoadingStatus] = useState(true);

  useEffect(() => {
    const loadStatus = async () => {
      try {
        const status = await getRetellStatus(agencyId, business.id);
        setRetellStatus(status);
      } catch (error) {
        console.error('Error loading Retell status:', error);
      } finally {
        setLoadingStatus(false);
      }
    };
    loadStatus();
  }, [agencyId, business.id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await updateAgencyBusiness(agencyId, business.id, {
        settings: { ...business.settings, maxCallDuration },
      });
      onUpdate();
      alert('Settings saved!');
    } catch (error) {
      console.error('Error updating settings:', error);
      alert('Failed to save settings');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Retell Configuration Status */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">AI Configuration Status</h3>
        {loadingStatus ? (
          <div className="animate-pulse space-y-3">
            <div className="h-12 bg-gray-200 rounded"></div>
            <div className="h-12 bg-gray-200 rounded"></div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <span className="text-sm text-gray-700">Retell AI Agent</span>
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                retellStatus?.hasAgent ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
              }`}>
                {retellStatus?.hasAgent ? 'Active' : 'Not Configured'}
              </span>
            </div>
            {retellStatus?.hasAgent && (
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <span className="text-sm text-gray-700">Agent ID</span>
                <span className="text-sm font-mono text-gray-900">{retellStatus.agentId}</span>
              </div>
            )}
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <span className="text-sm text-gray-700">Phone Number</span>
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                retellStatus?.hasPhoneNumber ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
              }`}>
                {retellStatus?.hasPhoneNumber ? retellStatus.phoneNumber : 'Not Assigned'}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Call Settings */}
      <form onSubmit={handleSubmit} className="border-t pt-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Call Settings</h3>
        <div>
          <label className="block text-sm font-medium text-gray-700">Max Call Duration (seconds)</label>
          <input
            type="number"
            min="60"
            max="3600"
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            value={maxCallDuration}
            onChange={(e) => setMaxCallDuration(parseInt(e.target.value))}
          />
          <p className="text-xs text-gray-500 mt-1">
            Maximum duration for calls (60-3600 seconds)
          </p>
        </div>
        <button type="submit" disabled={loading} className="mt-4 btn-primary">
          {loading ? 'Saving...' : 'Save Settings'}
        </button>
      </form>

      {/* Info */}
      <div className="border-t pt-6">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="text-sm font-medium text-blue-900 mb-2">About Retell AI</h4>
          <p className="text-sm text-blue-800">
            This business uses Retell AI for voice interactions. Retell handles speech recognition,
            natural language processing, and text-to-speech automatically. Configure the AI's behavior
            using the AI Configuration tab, and manage the voice and phone number in their respective tabs.
          </p>
        </div>
      </div>
    </div>
  );
}

// Delete Business Modal
function DeleteBusinessModal({
  business,
  agencyId,
  onClose,
  onDeleted,
}: {
  business: Business;
  agencyId: string;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const [loading, setLoading] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [retellStatus, setRetellStatus] = useState<RetellAgentStatus | null>(null);
  const [confirmText, setConfirmText] = useState('');

  useEffect(() => {
    const loadData = async () => {
      try {
        const status = await getRetellStatus(agencyId, business.id);
        setRetellStatus(status);
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setLoadingStatus(false);
      }
    };
    loadData();
  }, [agencyId, business.id]);

  const handleDelete = async () => {
    if (confirmText !== business.name) {
      alert('Please type the business name to confirm deletion');
      return;
    }

    setLoading(true);
    try {
      await deleteBusinessWithOptions(agencyId, business.id);
      onDeleted();
    } catch (error: any) {
      alert(error.message || 'Failed to delete business');
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={onClose}></div>
        <div className="relative bg-white rounded-lg max-w-lg w-full p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="flex-shrink-0 w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
              <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
            </div>
            <div>
              <h2 className="text-lg font-medium text-gray-900">Delete Business</h2>
              <p className="text-sm text-gray-500">This action cannot be undone</p>
            </div>
          </div>

          <div className="space-y-4">
            <p className="text-sm text-gray-700">
              You are about to permanently delete <strong>{business.name}</strong> and all associated data
              including calls, leads, and appointments.
            </p>

            {/* Retell Resources Warning */}
            {loadingStatus ? (
              <div className="text-sm text-gray-500">Checking AI configuration...</div>
            ) : (retellStatus?.hasAgent || retellStatus?.hasPhoneNumber) ? (
              <div className="border rounded-lg p-4 bg-yellow-50 border-yellow-200">
                <p className="text-sm font-medium text-yellow-800 mb-2">The following will also be deleted:</p>
                <ul className="text-sm text-yellow-700 space-y-1 list-disc list-inside">
                  {retellStatus.hasAgent && (
                    <li>AI Voice Agent ({retellStatus.agentId})</li>
                  )}
                  {retellStatus.hasPhoneNumber && (
                    <li>Phone Number ({retellStatus.phoneNumber})</li>
                  )}
                </ul>
              </div>
            ) : null}

            {/* Confirmation Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Type <strong>{business.name}</strong> to confirm
              </label>
              <input
                type="text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-red-500 focus:ring-red-500 sm:text-sm"
                placeholder={business.name}
              />
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={loading || confirmText !== business.name}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50"
              >
                {loading ? 'Deleting...' : 'Delete Business'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
